import React from 'react';
import { quickSummaryItems, termsSections, privacySections } from '../../content/legal';
import { downloadLegalPdf } from '../../utils/legalPdf';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  hasScrolledToEnd: boolean;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

const LegalModal: React.FC<LegalModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  hasScrolledToEnd,
  onScroll,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 p-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Terms & Privacy Summary</h2>
            <p className="mt-1 text-sm text-slate-600">Quick overview before you accept</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadLegalPdf}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close modal"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className="h-[60vh] overflow-y-auto p-6"
          onScroll={onScroll}
        >
          {/* Quick Summary */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Our Promise to You</h3>
            <div className="grid gap-3">
              {quickSummaryItems.map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Terms Sections */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Terms of Service</h3>
            <div className="space-y-4">
              {termsSections.map((section) => (
                <div key={section.title} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-sm font-semibold text-slate-900">{section.title}</p>
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph} className="mt-2 text-sm leading-6 text-slate-600">
                      {paragraph}
                    </p>
                  ))}
                  {section.bullets?.map((bullet) => (
                    <p key={bullet} className="mt-2 text-sm leading-6 text-slate-600">
                      {bullet}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Privacy Sections */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Privacy Policy</h3>
            <div className="space-y-4">
              {privacySections.map((section) => (
                <div key={section.title} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm font-semibold text-emerald-900">{section.title}</p>
                  {section.bullets?.map((bullet) => (
                    <p key={bullet} className="mt-2 text-sm leading-6 text-emerald-700">
                      {bullet}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6">
          <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
            {hasScrolledToEnd
              ? 'You have reached the end of the Terms and Privacy summary.'
              : 'Scroll to the bottom of the legal summary to enable the acceptance button.'}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onAccept}
              disabled={!hasScrolledToEnd}
              className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                hasScrolledToEnd
                  ? 'bg-slate-900 text-white hover:bg-slate-800'
                  : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
              }`}
            >
              I Agree to the Terms
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
